import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';
import { getRelatedDictionary } from '@/data/related';
import { Faq, paragraphs } from './RelatedSiteDetail';

/**
 * 関連事業 一覧(/related) — 2026-09-18 作り直し。
 * 導入 → 6 カード(実写・アカデミーのカード部品) → つながり(紺帯) → 目的別の入口 → FAQ → CTA。
 * 本文は src/data/related/{ja,en}.ts(辞書方式)。
 */

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function RelatedPage() {
  const { lang } = useLanguageContext();
  const dict = getRelatedDictionary(lang);
  const { common, hub, sites } = dict;
  const href = (path: string) => (lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path);

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: hub.hero.title,
    description: hub.hero.lead,
    url: 'https://butler-academy.jp/related',
    isPartOf: {
      '@type': 'WebSite',
      name: lang === 'en' ? 'Japan Butler Academy' : '日本執事アカデミー',
      url: 'https://butler-academy.jp/',
    },
    about: {
      '@type': 'Organization',
      name: lang === 'en' ? 'All Japan Butler Association' : '一般社団法人全日本執事協会',
      url: 'https://butler-association.jp/',
    },
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/related" jsonLd={[webPageStructuredData]} faqItems={hub.faq.map((f) => ({ question: f.q, answer: f.a }))} />

        {/* Hero */}
        <section className="hero-billboard relative w-full h-[64vh] min-h-[540px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
          <img src={hub.hero.image.src} alt={hub.hero.image.alt} className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 hero-overlay"></div>
          <div className="absolute inset-0 flex items-center pt-24 md:pt-0">
            <div className="relative w-full px-8">
              <div className="max-w-[800px] mx-auto text-center">
                <div className="hidden md:flex items-center justify-center gap-2 mb-8 text-[14px] tracking-[0.05em]">
                  <Link to={href('/')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {common.home}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <span className="text-accent-400">{common.parent}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 tracking-[0.08em] mb-4">{hub.hero.title}</h1>
                <p className="section-title-en text-accent-400 text-[15px] md:text-[17px] tracking-[0.12em] mb-6">{hub.hero.titleEn}</p>
                <div className="space-y-3 max-w-[620px] mx-auto">{paragraphs(hub.hero.lead, 250).map((t, k) => (<p key={k} className="text-[14px] md:text-[16px] text-foreground-200/80 tracking-[0.05em] leading-relaxed max-md:line-clamp-4">{t}</p>))}</div>
              </div>
            </div>
          </div>
        </section>
        <Breadcrumb items={[{ label: '関連事業', labelEn: 'Related Businesses' }]} />

        {/* Intro */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[860px] mx-auto px-8 text-center">
              <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{hub.intro.label}</p>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">{hub.intro.title}</h2>
              <p className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-5">{hub.intro.sub}</p>
              <div className="gold-line mx-auto mb-8"></div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose text-left">
                {paragraphs(hub.intro.paras).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {hub.intro.subs && (
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                  {hub.intro.subs.map((s, i) => (
                    <div key={i} className="border-l-2 border-accent-500/50 pl-5">
                      <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-2">{s.t}</h3>
                      {paragraphs(s.b).map((t, k) => (
                        <p key={k} className="text-[14px] md:text-[15px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-3 last:mb-0">{t}</p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {hub.intro.image && (
                <figure className="m-0 mt-12">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={hub.intro.image.src}
                      alt={hub.intro.image.alt}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ objectPosition: hub.intro.image.position ?? 'center' }}
                    />
                  </div>
                  <figcaption className="mt-2 text-[12px] text-foreground-400 tracking-[0.04em] text-left">{hub.intro.image.alt}</figcaption>
                </figure>
              )}
            </div>
          </section>
        </FadeInSection>

        {/* Cards */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-100/50">
            <div className="max-w-[1200px] mx-auto px-8">
              <div className="text-center mb-14">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{hub.cards.label}</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">{hub.cards.title}</h2>
                <p className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{hub.cards.sub}</p>
                <div className="gold-line mx-auto"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sites.map((s) => (
                  <div key={s.slug} className="card-glow group flex flex-col bg-background-50 border border-background-200/60 overflow-hidden">
                    <Link to={href(`/related/${s.slug}`)} className="relative aspect-[16/9] overflow-hidden block">
                      <img
                        src={s.hubImage.src}
                        alt={s.hubImage.alt}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        style={{ objectPosition: s.hubImage.position ?? 'center top' }}
                      />
                      <span className="absolute top-4 left-4 bg-primary-900/85 text-accent-400 text-[12px] tracking-[0.1em] px-3 py-1">{s.role}</span>
                    </Link>
                    <div className="flex flex-col flex-1 p-6">
                      <h3 className="font-heading text-[17px] text-foreground-900 tracking-[0.05em] mb-3 group-hover:text-accent-600 transition-colors">{s.name}</h3>
                      <p className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-6 flex-1">{s.tagline}</p>
                      <div className="flex flex-col gap-3 pt-4 border-t border-background-200/60">
                        <Link to={href(`/related/${s.slug}`)} className="flex items-center gap-2 text-accent-600 hover:text-accent-500 transition-colors">
                          <span className="text-[14px] tracking-[0.05em]">{common.viewInternal}</span>
                          <i className="ri-arrow-right-line text-xs"></i>
                        </Link>
                        <a href={s.officialUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-foreground-400 hover:text-accent-500 transition-colors">
                          <span className="text-[13px] tracking-[0.05em]">{common.viewExternal}</span>
                          <i className="ri-external-link-line text-xs"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Relationship */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-primary-900 text-foreground-200">
            <div className="max-w-[820px] mx-auto px-8">
              <div className="text-center mb-10">
                <p className="text-accent-400 text-[13px] tracking-[0.2em] mb-5 font-label">{hub.connection.label}</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-50 tracking-[0.06em] mb-2">{hub.connection.title}</h2>
                <p className="section-title-en text-accent-400 text-[15px] tracking-[0.12em] mb-6">{hub.connection.sub}</p>
                <div className="gold-line mx-auto"></div>
              </div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.03em] leading-loose">
                {paragraphs(hub.connection.paras).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {hub.connection.subs && (
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                  {hub.connection.subs.map((s, i) => (
                    <div key={i} className="border-l-2 border-accent-400/50 pl-5">
                      <h3 className="font-heading text-[16px] text-foreground-50 tracking-[0.05em] mb-2">{s.t}</h3>
                      {paragraphs(s.b).map((t, k) => (
                        <p key={k} className="text-[14px] md:text-[15px] text-foreground-200/75 tracking-[0.03em] leading-relaxed mb-3 last:mb-0">{t}</p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {hub.connection.image && (
              <div className="max-w-[1100px] mx-auto px-8 mt-14">
                <div className="relative aspect-[21/9] overflow-hidden">
                  <img
                    src={hub.connection.image.src}
                    alt={hub.connection.image.alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: hub.connection.image.position ?? 'center' }}
                  />
                </div>
                <p className="mt-2 text-[12px] text-foreground-200/60 tracking-[0.04em]">{hub.connection.image.alt}</p>
              </div>
            )}
          </section>
        </FadeInSection>

        {/* Paths by purpose */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[1100px] mx-auto px-8">
              <div className="text-center mb-12">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{hub.paths.label}</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">{hub.paths.title}</h2>
                <p className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{hub.paths.sub}</p>
                <div className="gold-line mx-auto"></div>
              </div>
              <div className="max-w-[860px] mx-auto mb-12 text-center space-y-4">{paragraphs(hub.paths.lede, 250).map((t, k) => (<p key={k} className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">{t}</p>))}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {hub.paths.items.map((it, i) => (
                  <div key={i} className="bg-background-100/50 border border-background-200/60 overflow-hidden flex flex-col">
                    {it.image && (
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={it.image.src}
                          alt={it.image.alt}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ objectPosition: it.image.position ?? 'center' }}
                        />
                      </div>
                    )}
                    <div className="p-7">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full border border-accent-500/40 text-accent-600 mb-5 font-heading text-[15px]">{String(i + 1).padStart(2, '0')}</div>
                      <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-3">{it.title}</h3>
                      {paragraphs(it.desc, 250).map((t, k) => (<p key={k} className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-2 last:mb-0">{t}</p>))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* FAQ */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-100/50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-12">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{common.faqLabel}</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">{common.faqTitle}</h2>
                <p className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{common.faqSub}</p>
                <div className="gold-line mx-auto"></div>
              </div>
              <Faq items={hub.faq} />
            </div>
          </section>
        </FadeInSection>

        {/* CTA */}
        <FadeInSection>
          <section className="relative py-20 md:py-24 bg-background-50">
            <div className="max-w-[760px] mx-auto px-8 text-center">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">{hub.cta.title}</h2>
              <p className="text-[15px] text-foreground-600 tracking-[0.03em] leading-relaxed mb-8">{hub.cta.body}</p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to={href('/courses')} className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors">
                  {hub.cta.courses}
                </Link>
                <Link to={href('/contact')} className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors">
                  {hub.cta.contact}
                </Link>
              </div>
            </div>
          </section>
        </FadeInSection>

        <div className="bg-background-100 py-8">
          <p className="text-center text-[13px] text-foreground-400 tracking-[0.05em]">{hub.lastUpdated}</p>
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={common.backToTop}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
